import React, { useEffect, useState } from "react";
import { Button, styled, Box, Stack, Typography } from "@mui/material";
import { useLazyQuery } from "@apollo/client";
import KeywordRankings from "./KeywordRankings";
import AnalyticsDashboard from "./AnalyticsDashboard";
import { GET_GSC_SITES } from "../../gql/queries/gsc";

const SiteSelectorWrapper = styled("section")(({ theme }) => ({
  marginTop: "20px",

  "& .selectorControls": {
    backgroundColor: theme.palette.mode === "dark" ? theme.palette.background.paper : "#ffffff",
    border: `1px solid ${theme.palette.divider}`,
    padding: "18px",
    borderRadius: "6px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "16px",
  },

  "& .siteSelectGroup": {
    display: "flex",
    alignItems: "center",
    gap: "10px",

    "& label": {
      fontSize: "13px",
      fontWeight: 600,
      color: theme.palette.text.primary,
    },

    "& select": {
      minWidth: "280px",
      padding: "10px 12px",
      borderRadius: "6px",
      border: `1px solid ${theme.palette.divider}`,
      backgroundColor: theme.palette.background.default,
      color: theme.palette.text.primary,
      fontSize: "14px",

      "&:focus": {
        outline: "none",
        borderColor: theme.palette.primary.main,
      },
    },
  },

  "& .viewToggle": {
    display: "flex",
    gap: "10px",

    "& .toggleBtn": {
      padding: "10px 18px",
      fontSize: "14px",
      fontWeight: 600,
      borderRadius: "6px",
      border: `1px solid ${theme.palette.primary.main}`,
      backgroundColor: "transparent",
      color: theme.palette.primary.main,
      transition: "all 0.2s ease",

      "&:hover": {
        backgroundColor: theme.palette.primary.dark,
        color: "#fff",
      },

      "&.active": {
        backgroundColor: theme.palette.primary.main,
        color: "#fff",
      },
    },
  },

  "& .loading": {
    textAlign: "center",
    padding: "40px",
    color: theme.palette.text.secondary,
  },

  "@media (max-width:600px)": {
    "& .selectorControls": {
      flexDirection: "column",
      alignItems: "stretch",
    },

    "& .siteSelectGroup select": {
      minWidth: "100%",
    },

    "& .viewToggle": {
      width: "100%",
      "& button": {
        flex: 1,
      },
    },
  },
}));

function SiteSelector({ email }) {
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState("");
  const [view, setView] = useState("keywords");
  const [loading, setLoading] = useState(true);

  const [loadSites] = useLazyQuery(GET_GSC_SITES, {
    fetchPolicy: "network-only",
    onCompleted: data => {
      if (data?.gscSites?.success) {
        const fetchedSites = data.gscSites.sites || [];
        setSites(fetchedSites);
        if (fetchedSites.length) {
          setSelectedSite(fetchedSites[0].siteUrl);
        }
      }
      setLoading(false);
    },
    onError: () => setLoading(false),
  });

  useEffect(() => {
    if (email) {
      loadSites({ variables: { email } });
    } else {
      setLoading(false);
    }
  }, [email, loadSites]);

  if (loading) {
    return (
      <SiteSelectorWrapper>
        <Box className="loading">Loading sites...</Box>
      </SiteSelectorWrapper>
    );
  }

  if (!sites.length) return null;

  return (
    <SiteSelectorWrapper>
      <Box className="selectorControls">
        <Box className="siteSelectGroup">
          <Typography component="label" htmlFor="select-site">
            Select Site
          </Typography>

          <Box component="select" id="select-site" value={selectedSite} onChange={e => setSelectedSite(e.target.value)}>
            {sites.map(site => (
              <option key={site.siteUrl} value={site.siteUrl}>
                {site.displayUrl || site.siteUrl}
              </option>
            ))}
          </Box>
        </Box>

        <Box className="viewToggle">
          <Button className={`toggleBtn ${view === "keywords" ? "active" : ""}`} onClick={() => setView("keywords")}>
            Keyword Rankings
          </Button>

          <Button className={`toggleBtn ${view === "analytics" ? "active" : ""}`} onClick={() => setView("analytics")}>
            Analytics Dashboard
          </Button>
        </Box>
      </Box>

      {selectedSite && (
        <>
          {view === "keywords" && <KeywordRankings email={email} defaultSite={selectedSite} />}
          {view === "analytics" && <AnalyticsDashboard email={email} siteUrl={selectedSite} />}
        </>
      )}
    </SiteSelectorWrapper>
  );
}

export default SiteSelector;
